import { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { ChevronDown, X, GitBranch, Database, List } from 'lucide-react';

export default function ValuesetPicker({ value, onChange, valuesets, className, triggerClassName, testId }) {
  const [open, setOpen] = useState(false);
  const selected = valuesets?.find(v => v.valueset_id === value);

  const handleSelect = (vsId) => {
    onChange(vsId === value ? null : vsId);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className={`justify-between rounded-none text-left font-normal ${triggerClassName || 'h-8 text-xs w-full'}`} data-testid={testId || 'valueset-picker'}>
          {selected ? (
            <span className="flex items-center gap-1.5 truncate">
              {selected.is_derived && <GitBranch className="w-3 h-3 text-[#002FA7] shrink-0" />}
              <span className="truncate">{selected.name}</span>
              <span className="text-[10px] text-[#9CA3AF] shrink-0">{selected.code_count}c</span>
            </span>
          ) : (
            <span className="text-[#9CA3AF]">No valueset</span>
          )}
          <div className="flex items-center gap-0.5 shrink-0 ml-1">
            {value && <X className="w-3 h-3 text-[#9CA3AF] hover:text-red-500" onClick={handleClear} />}
            <ChevronDown className="w-3 h-3 text-[#9CA3AF]" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none w-72" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Search valuesets..." className="h-8 text-xs rounded-none" />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-3 text-xs text-center text-[#9CA3AF]">No valuesets found</CommandEmpty>
            <CommandItem value="__none__" onSelect={() => { onChange(null); setOpen(false); }} className="text-xs py-2 px-3 rounded-none cursor-pointer" data-testid="vs-option-none">
              <span className="text-[#9CA3AF]">No valueset (free text)</span>
            </CommandItem>
            {(valuesets || []).map(vs => (
              <CommandItem key={vs.valueset_id} value={`${vs.name} ${vs.valueset_id}`} onSelect={() => handleSelect(vs.valueset_id)} className="py-2 px-3 rounded-none cursor-pointer" data-testid={`vs-option-${vs.valueset_id}`}>
                <div className="flex items-center gap-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {vs.is_derived ? <GitBranch className="w-3 h-3 text-[#002FA7] shrink-0" /> : <Database className="w-3 h-3 text-[#4B5563] shrink-0" />}
                      <span className="text-xs font-medium text-[#0A0A0A] truncate">{vs.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 pl-[18px]">
                      <span className="text-[10px] text-[#9CA3AF]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{vs.code_count} codes</span>
                      {vs.is_derived && <span className="text-[10px] px-1 bg-[#002FA7]/5 text-[#002FA7]">derived</span>}
                      {!vs.is_derived && vs.derived_count > 0 && <span className="text-[10px] px-1 bg-zinc-100 text-zinc-600">master</span>}
                      {vs.parent_name && <span className="text-[10px] text-[#9CA3AF] truncate">from {vs.parent_name}</span>}
                    </div>
                  </div>
                  {value === vs.valueset_id && <span className="text-[10px] text-[#002FA7] font-semibold shrink-0">Selected</span>}
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
